import React, { useEffect, useState } from 'react';
import { Button, Modal } from '@syneto/compass-react';
import { describeEditionLicenses, haveLicensesChanged, toLicensesPayload } from './editionDisplay';
import EditLicenseAction from './editLicenseAction';

/*
 * Edits what an edition gives: one row per license, with a tier picker. The licenses themselves
 * are not added or removed here - the three editions carry the seven edition licenses by
 * definition (never Multiple sockets), and what Operations tunes is the tier of each.
 *
 * Built out of Modal directly rather than on components/ActionModal, which has no way to disable
 * its primary action. Saving enqueues a job that takes a moment to apply, and without the disable
 * a second click fires a second job against the same edition.
 *
 * The picked tiers live in local state rather than the store: they are a draft until Save, and
 * Cancel throws them away. The modal stays open when the save fails, so the picks can be kept and
 * resubmitted once the problem is corrected.
 *
 * `suspended` hides the dialog without closing it, while the license form is open on top: the
 * component stays mounted, so the draft survives, and only one dialog is on screen at a time.
 */
const EditionEditModal = (props) => {
	const { edition, catalog, isSaving, suspended, onSave, onEditLicense, onCancel } = props;
	const [members, setMembers] = useState([]);

	useEffect(() => {
		setMembers(toLicensesPayload(edition?.licenses));
	}, [edition]);

	const rows = describeEditionLicenses({ licenses: members }, catalog);
	const hasChanges = haveLicensesChanged(edition?.licenses, members);

	const pickTier = (index, tierId) => {
		setMembers((current) => {
			return current.map((member, position) => {
				return position === index ? { ...member, tierId: tierId } : member;
			});
		});
	};

	const onConfirm = () => {
		return onSave(edition.id, toLicensesPayload(members));
	};

	return (
		<Modal show={Boolean(edition) && !suspended} size="lg" onHide={onCancel}>
			<Modal.Header>
				<Modal.Title>Edit edition</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				<dl className="row">
					<dt className="col-3">Edition</dt>
					<dd className="col-9">{edition?.name}</dd>
				</dl>

				<p className="text-value">Pick the tier each license gives in this edition:</p>

				{rows.length === 0
					? <p className="text-muted">This edition has no licenses.</p>
					: (
						<table className="table table-sm align-middle">
							<thead>
								<tr>
									<th scope="col">License</th>
									<th scope="col">Tier</th>
									{onEditLicense && <th scope="col" className="text-end">Actions</th>}
								</tr>
							</thead>
							<tbody>
								{rows.map((row, index) => {
									/*
									 * A tier the catalog does not list for this license (the catalog is stale, or
									 * the tier was removed) is kept as a disabled option, so the row still shows what
									 * the edition holds and the list is not silently rewritten on Save.
									 */
									const options = row.tierOptions.some((option) => { return option.value === row.tierId; })
										? row.tierOptions
										: [{ value: row.tierId, label: row.tierName, disabled: true }, ...row.tierOptions];

									return (
										<tr key={`${row.licenseId}-${index}`}>
											<td>{row.licenseName}</td>
											<td>
												<select
													className="form-select"
													aria-label={`Tier for ${row.licenseName}`}
													value={row.tierId}
													disabled={isSaving || row.tierOptions.length === 0}
													onChange={(event) => { return pickTier(index, event.target.value); }}
												>
													{options.map((option) => {
														return (
															<option key={option.value} value={option.value} disabled={option.disabled}>
																{option.label}
															</option>
														);
													})}
												</select>
											</td>
											{onEditLicense && (
												<td className="text-end">
													<EditLicenseAction row={row} onEditLicense={onEditLicense} />
												</td>
											)}
										</tr>
									);
								})}
							</tbody>
						</table>
					)}

				<p className="text-muted mb-0">
					The change applies to products that get the edition from now on. Products that already
					got it keep the licenses they have.
				</p>
			</Modal.Body>

			<Modal.Footer>
				<Button role="secondary" disabled={isSaving} onClick={onCancel}>Cancel</Button>
				<Button disabled={isSaving || !hasChanges} onClick={onConfirm}>Save</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default EditionEditModal;
